import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { adminApi } from "../../services/adminApi";
import { apiClient } from "../../services/apiClient";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { GapNotice } from "../../components/common/GapNotice";

export function FeatureAdoptionPage() {
  const [usage, setUsage] = useState(null);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    adminApi.featureUsage().then((r) => setUsage(r.feature_usage)).catch(() => setUsage([]));
    apiClient.get("/user/theme/available-features").then((r) => setCatalog(r.data.features)).catch(() => setCatalog([]));
  }, []);

  if (!usage || !catalog) return <Spinner />;

  const categoryByFeature = Object.fromEntries(catalog.map((f) => [f.feature_name, f.category]));
  const usedNames = new Set(usage.map((u) => u.feature_name));
  const enabledButUnused = catalog.filter((f) => f.default_enabled && !usedNames.has(f.feature_name));

  const byCategory = {};
  usage.forEach((u) => {
    const cat = categoryByFeature[u.feature_name] || "Other";
    byCategory[cat] = (byCategory[cat] || 0) + u.total_events;
  });
  const categoryChartData = Object.entries(byCategory).map(([category, total_events]) => ({ category, total_events }));

  return (
    <div>
      <h1>Feature Adoption Analytics</h1>

      <Card title="Feature Usage Heatmap (total events)" className="section-spacing">
        <ResponsiveContainer width="100%" height={Math.max(280, usage.length * 32)}>
          <BarChart data={usage} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="feature_name" width={160} />
            <Tooltip />
            <Legend />
            <Bar dataKey="completed" stackId="a" fill="#2e8b57" name="Completed" />
            <Bar dataKey="abandoned" stackId="a" fill="#d64545" name="Abandoned" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Usage by Category" className="section-spacing">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={categoryChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" angle={-20} textAnchor="end" height={70} />
            <YAxis />
            <Tooltip />
            <Bar dataKey="total_events" fill="#f2a900" name="Total Events" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Abandoned Features (enabled by default, never used)" className="section-spacing">
        <DataTable
          rows={enabledButUnused}
          rowKey="feature_name"
          emptyTitle="No abandoned features -- everything enabled has at least one recorded use"
          columns={[
            { key: "feature_name", header: "Feature" },
            { key: "category", header: "Category" },
            { key: "description", header: "Description" },
          ]}
        />
      </Card>

      <Card title="Feature Discovery vs. Usage">
        <GapNotice>
          app.FeatureUsageAnalytics only records events for features a user actually *used* -- there's no "discovery"
          signal (e.g. viewed a locked-feature preview, opened a settings toggle) logged anywhere, so "discovered but
          never used" can't be distinguished from "never seen at all" yet. Adding a lightweight discovery-impression
          event (mirroring web's LockedFeature/DidYouKnowTip components, which already render these moments in the UI)
          would close this.
        </GapNotice>
        <div style={{ marginTop: "var(--space-3)" }}>
          <Badge tone="info">Custom Theme Insights</Badge>
          <p style={{ color: "var(--color-text-muted)", marginTop: 4 }}>
            "Most commonly enabled features under the Custom theme" needs the feature-usage query filtered by
            selected_theme = 'Custom', which admin.py's current feature_usage endpoint doesn't take as a parameter.
          </p>
        </div>
      </Card>
    </div>
  );
}
